import { useEffect, useState } from "react";
import { X, ShieldCheck, Cookie } from "lucide-react";
import { Link } from "react-router-dom";
import {
  acceptAll,
  getConsent,
  hasDecidedConsent,
  rejectOptional,
  setConsent,
  subscribeConsent,
} from "@/lib/consent";

/**
 * Royal Lady — DPDP cookie / storage consent banner (P1-13)
 *
 * Design:
 *  - Bottom-anchored banner. Sticky, non-modal (doesn't block the app).
 *  - "Reject optional" + "Accept all" + "Customize" — three equally-weighted
 *    actions (Reject and Customize are NOT hidden behind a dropdown, which
 *    is required by DPDP §6 for a free & unambiguous choice).
 *  - "Customize" opens a small drawer with granular toggles:
 *      * Essential (always on, disabled toggle)
 *      * Analytics
 *      * Marketing
 *  - Learn more → /legal/privacy
 *  - Once the user decides, the banner unmounts. A `<CookiePreferencesLink>`
 *    lives in the footer so the user can revisit and change their choice
 *    later (DPDP: withdrawal must be as easy as giving consent).
 *
 * The banner never blocks essential shopping — closing without deciding
 * (via X) is treated as a soft-dismiss and does NOT record consent. Users
 * remain on the page and can shop; the banner re-appears on next visit.
 */

const Toggle = ({ label, description, checked, disabled = false, onChange, testId }) => (
  <label className={`flex cursor-pointer items-start gap-3 border border-gray-200 bg-white p-3 ${disabled ? "opacity-70" : "hover:border-black"}`}>
    <input
      type="checkbox"
      className="mt-1 h-4 w-4 accent-black"
      checked={!!checked}
      disabled={disabled}
      onChange={(e) => onChange && onChange(e.target.checked)}
      data-testid={testId}
    />
    <span>
      <div className="text-[12px] font-semibold uppercase tracking-widest text-gray-900">{label}</div>
      <div className="text-[11px] text-gray-500">{description}</div>
    </span>
  </label>
);

const Customize = ({ open, initial, onSave, onClose }) => {
  const [analytics, setAnalytics] = useState(!!initial?.analytics);
  const [marketing, setMarketing] = useState(!!initial?.marketing);
  useEffect(() => {
    if (open) {
      setAnalytics(!!initial?.analytics);
      setMarketing(!!initial?.marketing);
    }
  }, [open, initial]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      data-testid="consent-customize-modal"
    >
      <div className="w-full max-w-lg bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div className="flex items-center gap-2">
            <Cookie className="h-4 w-4 text-[#D4AF37]" />
            <h2 className="text-[12px] font-semibold uppercase tracking-widest text-gray-900">Cookie &amp; storage preferences</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="consent-customize-close"
            className="rounded-full p-1.5 text-gray-500 hover:bg-gray-100 hover:text-black"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-2 px-4 py-4">
          <Toggle
            label="Essential"
            description="Signing you in, keeping your bag, protecting the checkout. Always on."
            checked={true}
            disabled={true}
            testId="consent-toggle-essential"
          />
          <Toggle
            label="Analytics"
            description="Anonymous, aggregate stats (page views, conversion funnel) that help us improve. No IP, no user-agent, no third-party trackers."
            checked={analytics}
            onChange={setAnalytics}
            testId="consent-toggle-analytics"
          />
          <Toggle
            label="Marketing"
            description="Personalised offers, drops, and reminders. Sent only if you opt in."
            checked={marketing}
            onChange={setMarketing}
            testId="consent-toggle-marketing"
          />
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-4 py-3">
          <Link
            to="/legal/privacy"
            className="text-[11px] uppercase tracking-widest text-gray-500 underline underline-offset-2 hover:text-black"
            data-testid="consent-learn-more-link"
          >
            Learn more
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="border border-gray-300 bg-white px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-gray-700 hover:border-black"
              data-testid="consent-customize-cancel"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onSave({ analytics, marketing });
                onClose();
              }}
              className="bg-black px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-white transition-colors hover:bg-[#D4AF37] hover:text-black"
              data-testid="consent-customize-save"
            >
              Save preferences
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Sticky bottom banner shown only until a decision is made.
 * `forceOpen` allows the footer link to re-open it for editing.
 */
const CookieConsentBanner = ({ forceOpen = false, onDecide, onCloseCustomize }) => {
  const [visible, setVisible] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [current, setCurrent] = useState(getConsent());

  useEffect(() => {
    // On mount decide whether to show. If the user has already decided, we
    // keep the banner hidden and let the footer link reopen it explicitly.
    const decided = hasDecidedConsent();
    setVisible(forceOpen || !decided);
    if (forceOpen) setCustomize(true);
    // React to external updates (multi-tab / preferences change).
    const unsub = subscribeConsent((c) => setCurrent(c));
    return unsub;
  }, [forceOpen]);

  const decide = (payload) => {
    if (onDecide) onDecide(payload);
    setVisible(false);
  };

  const bannerVisible = visible && !customize;

  return (
    <>
      {bannerVisible && (
        <div
          className="fixed inset-x-0 bottom-0 z-[9999] border-t border-[#D4AF37]/40 bg-black text-white shadow-[0_-6px_20px_rgba(0,0,0,0.2)]"
          role="dialog"
          aria-label="Cookie consent"
          data-testid="consent-banner"
        >
          <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:py-4">
            <div className="flex items-start gap-2">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" aria-hidden="true" />
              <p className="text-[12px] leading-relaxed text-white/90">
                Royal Lady uses <b>essential</b> storage to sign you in and hold your bag.
                We&apos;d also like your consent for <b>anonymous analytics</b> so we can
                improve the experience. Optional consent can be revoked anytime from
                <span className="whitespace-nowrap">
                  {" "}
                  <Link
                    to="/legal/privacy"
                    className="underline underline-offset-2 hover:text-[#EACB7A]"
                    data-testid="consent-privacy-link"
                  >
                    Privacy
                  </Link>
                  {" "}or Cookie preferences.
                </span>
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                data-testid="consent-reject-btn"
                onClick={() => { rejectOptional(); decide({ analytics: false, marketing: false }); }}
                className="border border-white/60 bg-black px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-white hover:border-[#D4AF37] hover:text-[#EACB7A]"
              >
                Reject optional
              </button>
              <button
                data-testid="consent-customize-btn"
                onClick={() => setCustomize(true)}
                className="border border-white/60 bg-black px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-white hover:border-[#D4AF37] hover:text-[#EACB7A]"
              >
                Customize
              </button>
              <button
                data-testid="consent-accept-btn"
                onClick={() => { acceptAll(); decide({ analytics: true, marketing: true }); }}
                className="bg-[#D4AF37] px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-black hover:bg-[#EACB7A]"
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      )}
      <Customize
        open={customize}
        initial={current}
        onSave={(v) => {
          setConsent(v, { source: "customize" });
          if (onDecide) onDecide(v);
        }}
        onClose={() => {
          setCustomize(false);
          // If they closed customize without saving AND haven't decided yet,
          // keep the banner visible until they explicitly decide.
          if (!hasDecidedConsent() && !forceOpen) setVisible(true);
          if (onCloseCustomize) onCloseCustomize();
        }}
      />
    </>
  );
};

export default CookieConsentBanner;
